import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '@/layouts/AdminLayout'
import toast from 'react-hot-toast'
import { tarifService, authService } from '@services/api'
import { settingsService } from '@services/api/settings'
import apiClient from '@services/api/client'

const AdminSettingsPage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [adminData, setAdminData] = useState(null)
  const [isAdminOrSuperAdmin, setIsAdminOrSuperAdmin] = useState(false)
  
  const [settingsData, setSettingsData] = useState({
    notifications: true,
    emailNotifications: true,
    smsNotifications: false,
    language: 'fr',
    timezone: 'Africa/Abidjan'
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [tarifs, setTarifs] = useState({
    articles: [],
    videos: [],
    banners: []
  })

  const [activeSection, setActiveSection] = useState('notifications')
  const [freeTrialDays, setFreeTrialDays] = useState(30)
  const [savingTrial, setSavingTrial] = useState(false)

  // Charger les données admin
  useEffect(() => {
    const admin = localStorage.getItem('admin_user')
    if (admin) {
      try {
        const adminUser = JSON.parse(admin)
        setAdminData(adminUser)
        
        // Vérifier si l'utilisateur est Admin ou Super Admin
        // Le rôle peut être sous plusieurs formes : roles[] (chaînes), role (objet {name}) ou role (chaîne)
        const roleObj = adminUser.role
        const roleName = (typeof roleObj === 'object' && roleObj !== null) ? roleObj.name : roleObj
        const userRoles = Array.isArray(adminUser.roles) && adminUser.roles.length
          ? adminUser.roles
          : (roleName ? [roleName] : [])
        const hasAccess = userRoles.some(r =>
          ['super admin', 'admin'].includes(String(r).toLowerCase())
        )
        setIsAdminOrSuperAdmin(hasAccess)
        
        // Charger les paramètres existants
        fetchSettings(adminUser.users_id)
        
        // Charger les tarifs
        fetchTarifs()

        // Charger les réglages globaux (durée période d'essai)
        if (hasAccess) fetchAppSettings()
      } catch (error) {
        console.error('Erreur:', error)
      }
    }
  }, [])

  const fetchAppSettings = async () => {
    try {
      const res = await settingsService.getApp()
      if (res.success && res.data) {
        setFreeTrialDays(res.data.free_trial_days ?? 30)
      }
    } catch (error) {
      console.error('Erreur chargement réglages:', error)
    }
  }

  const handleSaveTrial = async () => {
    const days = parseInt(freeTrialDays, 10)
    if (isNaN(days) || days < 0 || days > 365) {
      toast.error('Durée invalide (0 à 365 jours)')
      return
    }
    setSavingTrial(true)
    try {
      const res = await settingsService.updateApp({ free_trial_days: days })
      if (res.success) {
        setFreeTrialDays(res.data?.free_trial_days ?? days)
        toast.success("Durée de la période d'essai enregistrée")
      }
    } catch (error) {
      console.error('Erreur:', error)
      toast.error("Erreur lors de l'enregistrement")
    } finally {
      setSavingTrial(false)
    }
  }

  // Détecter la section active basée sur le scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['notifications', 'security', 'preferences', 'system', 'email', 'tarifs']
      const scrollPosition = window.scrollY + 200

      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Vérifier la position initiale

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const fetchSettings = async (userId) => {
    try {
      const data = await apiClient.get('/profile/settings')
      if (data.success && data.data) {
        setSettingsData({
          notifications: data.data.notifications || false,
          emailNotifications: data.data.notifications_email || false,
          smsNotifications: data.data.notifications_sms || false,
          language: data.data.language || 'fr',
          timezone: data.data.timezone || 'Africa/Abidjan'
        })
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error)
    }
  }

  const mapTarif = (t) => ({
    id: t.IdTarifs,
    price: t.montant_Tarifs,
    days: t.nb_jours,
    imageCount: t.nb_images,
    label: t.libelle_Tarifs,
    code: t.code_Tarifs,
    package: t.indification_package,
  })

  const fetchTarifs = async () => {
    try {
      const result = await tarifService.getTarifs()
      const data = result.data || {}
      setTarifs({
        articles: [
          ...(data.I3 || []).map(mapTarif),
          ...(data.I6 || []).map(mapTarif),
          ...(data.I9 || []).map(mapTarif),
        ],
        videos: (data.V || []).map(mapTarif),
        banners: (data.B || []).map(mapTarif),
      })
    } catch (error) {
      console.error('Erreur lors du chargement des tarifs:', error)
      toast.error('Erreur lors du chargement des tarifs')
    }
  }

  const handleSaveSettings = async () => {
    setLoading(true)
    try {
      await authService.updateProfile(settingsData)
      toast.success('Paramètres enregistrés avec succès !')
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur de connexion au serveur')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()

    if (!adminData?.users_id) {
      toast.error('Session expirée')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas')
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères')
      return
    }

    setLoading(true)
    try {
      await authService.changePassword({
        current_password: passwordData.currentPassword,
        password: passwordData.newPassword,
        password_confirmation: passwordData.confirmPassword
      })
      toast.success('Mot de passe modifié avec succès !')
      setShowPasswordModal(false)
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur de connexion au serveur')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateTarif = (type, tarifId, newPrice) => {
    setTarifs(prev => ({
      ...prev,
      [type]: prev[type].map(tarif => 
        tarif.id === tarifId ? { ...tarif, price: newPrice } : tarif
      )
    }))
  }

  const handleSaveTarifs = async () => {
    setLoading(true)
    try {
      const allTarifs = [
        ...tarifs.articles,
        ...tarifs.videos,
        ...tarifs.banners,
      ]
      await Promise.all(allTarifs.map(tarif =>
        tarifService.updateTarif(tarif.id, {
          montant_Tarifs: tarif.price,
          nb_jours: tarif.days,
          nb_images: tarif.imageCount,
          libelle_Tarifs: tarif.label,
          code_Tarifs: tarif.code,
          indification_package: tarif.package,
        })
      ))
      toast.success('Tarifs mis à jour avec succès !')
      fetchTarifs()
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur de connexion au serveur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AdminLayout>
      <div className="w-full mx-auto mt-10">
        {/* <div className="mb-8">
          <h1 className="text-4xl font-bold text-gradient-ligdi mb-2">Paramètres Administrateur</h1>
          <p className="text-gray-600">Gérez vos préférences et la sécurité de votre compte administrateur</p>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu latéral */}
          <div className="lg:col-span-1">
            <div className="card-ligdi space-y-2 sticky top-28">
              <a href="#notifications" className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${activeSection === 'notifications' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'hover:bg-gray-50 text-gray-700'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span>Notifications</span>
              </a>
              <a href="#security" className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${activeSection === 'security' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'hover:bg-gray-50 text-gray-700'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Sécurité</span>
              </a>
              <a href="#preferences" className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${activeSection === 'preferences' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'hover:bg-gray-50 text-gray-700'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Préférences</span>
              </a>
              {isAdminOrSuperAdmin && (
                <>
              <a href="#system" className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${activeSection === 'system' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'hover:bg-gray-50 text-gray-700'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                <span>Système</span>
              </a>
              <a href="#email" className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${activeSection === 'email' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'hover:bg-gray-50 text-gray-700'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Configuration Email</span>
              </a>
              <a href="#tarifs" className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${activeSection === 'tarifs' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'hover:bg-gray-50 text-gray-700'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Tarifs</span>
              </a>
                </>
              )}
            </div>
          </div>

          {/* Contenu principal */}
          <div className="lg:col-span-2 space-y-6">{/* Section Notifications */}
            <div id="notifications" className="card-ligdi">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#001F3F]">Notifications</h2>
                  <p className="text-sm text-gray-600">Gérez vos préférences de notifications administrateur</p>
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Activer les notifications système</div>
                    <div className="text-sm text-gray-600">Recevoir des notifications sur les activités du système</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.notifications}
                    onChange={(e) => setSettingsData({...settingsData, notifications: e.target.checked})}
                    className="w-5 h-5 text-[#D4AF37] rounded focus:ring-2 focus:ring-[#D4AF37]"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Notifications par email</div>
                    <div className="text-sm text-gray-600">Recevoir des emails pour les événements importants</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.emailNotifications}
                    onChange={(e) => setSettingsData({...settingsData, emailNotifications: e.target.checked})}
                    className="w-5 h-5 text-[#D4AF37] rounded focus:ring-2 focus:ring-[#D4AF37]"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">Alertes SMS</div>
                    <div className="text-sm text-gray-600">Recevoir des SMS pour les alertes critiques</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settingsData.smsNotifications}
                    onChange={(e) => setSettingsData({...settingsData, smsNotifications: e.target.checked})}
                    className="w-5 h-5 text-[#D4AF37] rounded focus:ring-2 focus:ring-[#D4AF37]"
                  />
                </label>
              </div>
            </div>

            {/* Section Sécurité */}
            <div id="security" className="card-ligdi">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#001F3F]">Sécurité</h2>
                  <p className="text-sm text-gray-600">Protégez votre compte administrateur</p>
                </div>
              </div>

              <div className="space-y-4">
                <button 
                  onClick={() => setShowPasswordModal(true)}
                  className="w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-800">Changer le mot de passe</div>
                    <div className="text-sm text-gray-600">Modifiez votre mot de passe administrateur</div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-800 mb-1">Authentification à deux facteurs</h3>
                      <p className="text-sm text-blue-700 mb-3">Ajoutez une couche de sécurité supplémentaire à votre compte</p>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm">
                        Activer 2FA (Bientôt disponible)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-800 mb-1">Sessions actives</h3>
                      <p className="text-sm text-amber-700 mb-3">Gérez les appareils connectés à votre compte</p>
                      <button className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm">
                        Voir les sessions
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Préférences */}
            <div id="preferences" className="card-ligdi">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#001F3F]">Préférences</h2>
                  <p className="text-sm text-gray-600">Personnalisez votre espace administrateur</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Langue de l'interface</label>
                  <select
                    value={settingsData.language}
                    onChange={(e) => setSettingsData({...settingsData, language: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] bg-white"
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuseau horaire</label>
                  <select
                    value={settingsData.timezone}
                    onChange={(e) => setSettingsData({...settingsData, timezone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] bg-white"
                  >
                    <option value="Africa/Abidjan">Afrique/Abidjan (GMT+0)</option>
                    <option value="Africa/Dakar">Afrique/Dakar (GMT+0)</option>
                    <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section Système - Admin et Super Admin uniquement */}
            {isAdminOrSuperAdmin && (
            <div id="system" className="card-ligdi">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#001F3F]">Paramètres Système</h2>
                  <p className="text-sm text-gray-600">Configuration générale de la plateforme</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du site</label>
                  <input
                    type="text"
                    defaultValue="KUDHI"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    placeholder="Nom de votre plateforme"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email de contact</label>
                  <input
                    type="email"
                    defaultValue="contact@kudhi.net"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    defaultValue="+225 XX XX XX XX XX"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    placeholder="+225 XX XX XX XX XX"
                  />
                </div>

                {/* Durée de la période d'essai */}
                <div className="p-4 bg-[#D4AF37]/5 rounded-lg border border-[#D4AF37]/30">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée de la période d'essai (jours)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Nombre de jours de période gratuite offerts à chaque nouvelle inscription.
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={freeTrialDays}
                      onChange={(e) => setFreeTrialDays(e.target.value)}
                      className="w-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    />
                    <span className="text-sm text-gray-600">jours</span>
                    <button
                      type="button"
                      onClick={handleSaveTrial}
                      disabled={savingTrial}
                      className="ml-auto px-5 py-2.5 bg-[#D4AF37] hover:bg-[#c09d2a] text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {savingTrial ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Enregistrement...
                        </>
                      ) : 'Enregistrer'}
                    </button>
                  </div>
                </div>

                <label className="flex items-center justify-between p-4 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors border border-amber-200">
                  <div className="flex-1">
                    <div className="font-medium text-amber-800">Mode Maintenance</div>
                    <div className="text-sm text-amber-700">Désactiver temporairement le site pour maintenance</div>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-[#D4AF37] rounded focus:ring-2 focus:ring-[#D4AF37]"
                  />
                </label>
              </div>
            </div>
            )}

            {/* Section Email - Admin et Super Admin uniquement */}
            {isAdminOrSuperAdmin && (
            <div id="email" className="card-ligdi">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#001F3F]">Configuration Email</h2>
                  <p className="text-sm text-gray-600">Paramètres SMTP pour l'envoi d'emails</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Serveur SMTP</label>
                  <input
                    type="text"
                    placeholder="smtp.exemple.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Port</label>
                    <input
                      type="number"
                      defaultValue="587"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Encryption</label>
                    <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] bg-white">
                      <option value="tls">TLS</option>
                      <option value="ssl">SSL</option>
                      <option value="none">Aucun</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom d'utilisateur</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    placeholder="utilisateur@domaine.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
                  <input
                    type="password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    placeholder="••••••••"
                  />
                </div>

                <button className="w-full px-4 py-3 bg-cyan-50 text-cyan-700 rounded-lg hover:bg-cyan-100 transition-colors font-medium border border-cyan-200">
                  📧 Envoyer un email de test
                </button>
              </div>
            </div>
            )}

            {/* Section Tarifs - Admin et Super Admin uniquement */}
            {isAdminOrSuperAdmin && (
            <div id="tarifs" className="card-ligdi">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#001F3F]">Gestion des Tarifs</h2>
                  <p className="text-sm text-gray-600">Prix des publications par type et durée</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Tarifs Articles */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white text-xl">
                      📄
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Articles</h4>
                      <p className="text-sm text-gray-600">Tarifs par durée et nombre d'images</p>
                    </div>
                  </div>
                  {tarifs.articles.length > 0 ? (
                    <div className="space-y-3">
                      {/* Grouper par nombre d'images */}
                      {[3, 6, 9].map(imageCount => {
                        const articlesByImages = tarifs.articles.filter(t => t.imageCount === imageCount)
                        if (articlesByImages.length === 0) return null
                        return (
                          <div key={imageCount} className="bg-white rounded-lg p-4 border border-gray-200">
                            <p className="text-xs font-semibold text-gray-700 mb-3">{imageCount} images</p>
                            <div className="grid grid-cols-4 gap-2">
                              {articlesByImages.map((tarif) => (
                                <div key={tarif.id} className="bg-gray-50 rounded p-2">
                                  <p className="text-xs text-gray-600 mb-1">{tarif.days} jour{tarif.days > 1 ? 's' : ''}</p>
                                  <div className="flex items-center gap-1">
                                    <input 
                                      type="number" 
                                      value={tarif.price} 
                                      onChange={(e) => handleUpdateTarif('articles', tarif.id, parseFloat(e.target.value) || 0)}
                                      className="w-full px-1 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-[#D4AF37]" 
                                    />
                                    <span className="text-xs text-gray-600 whitespace-nowrap">F</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      Chargement des tarifs...
                    </div>
                  )}
                </div>

                {/* Tarifs Vidéos */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center text-white text-xl">
                      🎥
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Vidéos</h4>
                      <p className="text-sm text-gray-600">Tarifs par durée de publication</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {tarifs.videos.length > 0 ? (
                      tarifs.videos.map((tarif) => (
                        <div key={tarif.id} className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-600 mb-1">{tarif.days} jour{tarif.days > 1 ? 's' : ''}</p>
                          <div className="flex items-center gap-1">
                            <input 
                              type="number" 
                              value={tarif.price} 
                              onChange={(e) => handleUpdateTarif('videos', tarif.id, parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#D4AF37]" 
                            />
                            <span className="text-xs text-gray-600 whitespace-nowrap">FCFA</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-4 text-center py-4 text-gray-500 text-sm">
                        Chargement des tarifs...
                      </div>
                    )}
                  </div>
                </div>

                {/* Tarifs Bannières */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white text-xl">
                      📢
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Bannières</h4>
                      <p className="text-sm text-gray-600">Tarifs par durée de publication</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {tarifs.banners.length > 0 ? (
                      tarifs.banners.map((tarif) => (
                        <div key={tarif.id} className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-600 mb-1">{tarif.days} jour{tarif.days > 1 ? 's' : ''}</p>
                          <div className="flex items-center gap-1">
                            <input 
                              type="number" 
                              value={tarif.price} 
                              onChange={(e) => handleUpdateTarif('banners', tarif.id, parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#D4AF37]" 
                            />
                            <span className="text-xs text-gray-600 whitespace-nowrap">FCFA</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-4 text-center py-4 text-gray-500 text-sm">
                        Chargement des tarifs...
                      </div>
                    )}
                  </div>
                </div>

                {/* Bouton sauvegarder les tarifs */}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleSaveTarifs}
                    disabled={loading}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Enregistrer les tarifs
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            )}

            {/* Bouton Enregistrer */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="px-8 py-3 bg-[#D4AF37] text-white rounded-lg font-medium hover:bg-[#c09d2a] transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Enregistrer les paramètres
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Changement de mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#001F3F]">Changer le mot de passe</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe actuel</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  required
                  minLength={6}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Changement...' : 'Confirmer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminSettingsPage
